import { useMemo } from 'react';
import type {
  ArcProperties,
  KokoroAgentGender,
  KokoroBlendMode,
  KokoroGender,
  KokoroPin,
  VoiceSpec,
} from '../zerovoice/types';
import {
  KOKORO_VOICES,
  resolveKokoroVoice,
  isValidKokoroVoiceId,
} from '../zerovoice/voiceMapping';
import type { KokoroVoiceId } from '../zerovoice/voiceMapping';
import { resolveKokoroSpec, formatSpec } from '../zerovoice/kokoroSpec';
import { parseHexSeed } from '../zerovoice/xxhash';

interface Props {
  agentName: string;
  worldSeedHex: string;
  defaultGender: KokoroGender;
  blendMode: KokoroBlendMode;
  /** Only consulted when blendMode === 'blend'. */
  arcTable: ArcProperties[][] | null;
  /** Undefined until xxhash loaded. When false, rows render a dash. */
  xxhashReady: boolean;
  pin: KokoroPin | undefined;
  onGenderChange: (gender: KokoroAgentGender) => void;
  onPinToggleSingle: (voiceId: KokoroVoiceId, gender: KokoroGender) => void;
  onPinToggleBlend: (spec: VoiceSpec, gender: KokoroGender) => void;
  onPlaySingle: (voiceId: KokoroVoiceId) => void;
  onPlayBlend: (spec: VoiceSpec) => void;
  /** Stage 12 (E3): open the Customize Spec dialog. Only rendered when set
   *  and blendMode === 'blend'. */
  onCustomize?: (initialSpec: VoiceSpec | null) => void;
  playingKey: string | null;
}

function parsePinnedSpec(pin: KokoroPin | undefined): VoiceSpec | null {
  const raw = pin?.spec_json;
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<VoiceSpec>;
    if (
      typeof parsed.voiceA === 'number' &&
      typeof parsed.voiceB === 'number' &&
      typeof parsed.t === 'number' &&
      typeof parsed.pitch === 'number' &&
      typeof parsed.energy === 'number'
    ) {
      return parsed as VoiceSpec;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function ZeroVoiceAgentRow(props: Props) {
  const {
    agentName,
    worldSeedHex,
    defaultGender,
    blendMode,
    xxhashReady,
    pin,
    onGenderChange,
    onPinToggleSingle,
    onPinToggleBlend,
    onPlaySingle,
    onPlayBlend,
    onCustomize,
    playingKey,
  } = props;

  const effectiveGender: KokoroGender = pin?.gender ?? defaultGender;
  const pinnedSpec = parsePinnedSpec(pin);

  // Resolve for the current mode. In blend mode, pinned spec wins; otherwise
  // hash-derive fresh. In single mode, fall through to the Stage 7 logic.
  const resolvedSpec = useMemo<VoiceSpec | null>(() => {
    if (blendMode !== 'blend') return null;
    if (pinnedSpec) return pinnedSpec;
    if (!xxhashReady) return null;
    try {
      return resolveKokoroSpec({
        name: agentName,
        gender: effectiveGender,
        worldSeed: parseHexSeed(worldSeedHex || '0'),
      });
    } catch {
      return null;
    }
  }, [agentName, worldSeedHex, effectiveGender, xxhashReady, blendMode, pinnedSpec]);

  const resolvedVoice = useMemo<KokoroVoiceId | null>(() => {
    if (blendMode === 'blend') return null;
    if (pin && isValidKokoroVoiceId(pin.voice_id)) return pin.voice_id;
    if (!xxhashReady) return null;
    try {
      return resolveKokoroVoice({
        name: agentName,
        gender: effectiveGender,
        worldSeed: parseHexSeed(worldSeedHex || '0'),
      });
    } catch {
      return null;
    }
  }, [agentName, worldSeedHex, effectiveGender, xxhashReady, blendMode, pin]);

  const genderValue: KokoroAgentGender = pin?.gender ?? 'default';
  const pinned = !!pin;

  const rowKey =
    blendMode === 'blend'
      ? resolvedSpec
        ? `${agentName}:blend`
        : null
      : resolvedVoice;
  const playing = playingKey !== null && playingKey === rowKey;

  return (
    <tr>
      <td>{agentName}</td>
      <td>
        <select
          value={genderValue}
          onChange={(e) => onGenderChange(e.target.value as KokoroAgentGender)}
        >
          <option value="default">default ({defaultGender})</option>
          <option value="female">female</option>
          <option value="male">male</option>
          <option value="mixed">mixed</option>
        </select>
      </td>
      <td>
        {blendMode === 'blend' ? (
          <code title={resolvedSpec ? formatSpec(resolvedSpec) : undefined}>
            {resolvedSpec
              ? `${KOKORO_VOICES[resolvedSpec.voiceA]} × ${KOKORO_VOICES[resolvedSpec.voiceB]} @ ${resolvedSpec.t.toFixed(2)}`
              : '—'}
          </code>
        ) : (
          <code>{resolvedVoice ?? '—'}</code>
        )}
      </td>
      <td>
        <button
          type="button"
          aria-pressed={pinned}
          onClick={() => {
            if (blendMode === 'blend') {
              if (!resolvedSpec) return;
              onPinToggleBlend(resolvedSpec, effectiveGender);
            } else {
              if (!resolvedVoice) return;
              onPinToggleSingle(resolvedVoice, effectiveGender);
            }
          }}
          disabled={blendMode === 'blend' ? !resolvedSpec : !resolvedVoice}
          title={
            pinned
              ? 'Unpin (revert to hash-derived)'
              : blendMode === 'blend'
                ? 'Pin this blend (survives world-seed changes)'
                : 'Pin this voice (survives world-seed changes)'
          }
        >
          {pinned ? 'Pinned' : 'Pin'}
        </button>
      </td>
      <td>
        <button
          type="button"
          onClick={() => {
            if (blendMode === 'blend' && resolvedSpec) {
              onPlayBlend(resolvedSpec);
            } else if (resolvedVoice) {
              onPlaySingle(resolvedVoice);
            }
          }}
          disabled={
            playingKey !== null ||
            (blendMode === 'blend' ? !resolvedSpec : !resolvedVoice)
          }
          aria-label={`Test voice for ${agentName}`}
        >
          {playing ? '…' : '▶'}
        </button>
        {blendMode === 'blend' && onCustomize ? (
          <button
            type="button"
            onClick={() => onCustomize(resolvedSpec)}
            title="Customise this agent's blend manually"
            aria-label={`Customise voice for ${agentName}`}
            style={{ marginLeft: 4 }}
          >
            ✎
          </button>
        ) : null}
      </td>
    </tr>
  );
}
